import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Sparkles, Infinity as InfinityIcon, BookOpen, Users } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { API_BASE } from '@/lib/api';

const LOCAL_KEY = 'kinsy_proactive_upsell_shown_v1';

/**
 * Fires ONCE when a free-plan family crosses 80% of their photo quota
 * (40/50 photos). Catches users mid-upload while they're still engaged,
 * before they hit the hard wall at 50/50.
 *
 * Dismissal is sticky per device (localStorage), so we never nag the
 * same person more than once.
 */
export default function ProactiveUpsellModal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    if (!user?.family_id) return;
    if (localStorage.getItem(LOCAL_KEY)) return;  // already shown

    const checkUsage = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/billing/usage`, { withCredentials: true });
        // Only show for free plan, when 40-49 photos used (>= 80%, < 100%)
        const used = data.photos_used || 0;
        const limit = data.photos_limit;
        if ((data.plan === 'free' || data.plan_id === 'free') && limit && used >= Math.floor(limit * 0.8) && used < limit) {
          setUsage({ used, limit });
          setOpen(true);
          localStorage.setItem(LOCAL_KEY, new Date().toISOString());
        }
      } catch {
        /* silent */
      }
    };

    // Wait 2.5s after auth load so we don't pop on first-screen render
    const t = setTimeout(checkUsage, 2500);
    return () => clearTimeout(t);
  }, [user?.family_id]);

  const upgrade = () => {
    setOpen(false);
    navigate('/pricing');
  };

  const dismiss = () => {
    setOpen(false);
  };

  if (!usage) return null;

  const pct = Math.round((usage.used / usage.limit) * 100);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md rounded-3xl border-0 p-0 overflow-hidden" data-testid="proactive-upsell-modal">
        {/* Teal banner */}
        <div className="bg-gradient-to-br from-teal-500 to-teal-700 px-6 py-5 text-white">
          <div className="flex items-center gap-2 mb-1.5">
            <Sparkles className="w-4 h-4" />
            <span className="text-xs font-semibold tracking-wide uppercase opacity-90">You&apos;re building something special</span>
          </div>
          <h2 className="text-xl font-bold leading-tight">
            {usage.used} of {usage.limit} photos used
          </h2>
          {/* Progress bar */}
          <div className="mt-3 h-1.5 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-white rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>

        {/* Pitch */}
        <div className="p-6">
          <p className="text-sm text-stone-700 leading-relaxed mb-5">
            Looks like your family is loving Kinsy. Upgrade to <strong className="text-teal-700">Family Plan</strong> before
            you hit the limit and unlock the good stuff:
          </p>
          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                <InfinityIcon className="w-4 h-4 text-teal-600" />
              </div>
              <div className="text-sm text-stone-700"><strong className="text-stone-900">Unlimited photos &amp; videos</strong> — no more counting</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                <Users className="w-4 h-4 text-teal-600" />
              </div>
              <div className="text-sm text-stone-700"><strong className="text-stone-900">Invite all of your family</strong> — even the in-laws</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                <BookOpen className="w-4 h-4 text-teal-600" />
              </div>
              <div className="text-sm text-stone-700"><strong className="text-stone-900">10% off every photo book</strong> shipped to your door</div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Button
              onClick={upgrade}
              className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-semibold"
              data-testid="proactive-upgrade-btn"
            >
              See plans
            </Button>
            <Button
              variant="ghost"
              onClick={dismiss}
              className="w-full h-10 text-stone-500 hover:text-stone-700 rounded-full text-sm"
              data-testid="proactive-dismiss-btn"
            >
              Not right now
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
